// File: app/src/components/plan/StepAmountFields.js
//
// How much of a step: its unit, its value and its load, set the same way
// wherever a step's numbers are set — the plan editor's step dialog (every
// session, programme and history edit) and Free training's log sheets
// (#164). The unit is a row of choices showing every option, the value and
// the load are draggable steppers.

import React, { useState } from "react";
import DraggableStepper from "../ui/draggable-stepper";
import UnitToggle from "../ui/UnitToggle";
import { useWeightUnit } from "../../contexts/WeightUnitContext";
import { fromDisplayWeight, toDisplayWeight } from "../../utils/units";
import { ChoiceChip } from "../profile/ProfileParts";
import { cn } from "../../lib/utils";

export const UNIT_LABELS = {
  s: "Seconds",
  m: "Minutes",
  reps: "Reps",
  cal: "Calories",
  rounds: "Rounds",
  sets: "Sets",
  workout: "Workout",
};

// The stepper for each unit: its label, range, step and what it shows.
const AMOUNT = {
  reps: { label: "Reps", min: 1, max: 200, step: 1, unit: "reps", start: 10 },
  s: { label: "Time", min: 5, max: 7200, step: 5, unit: "s", start: 30, momentum: true },
  // Seconds and minutes as one choice (#181): shown as m:ss, stored in
  // whichever unit reads best (see splitTime).
  time: { label: "Time", min: 5, max: 14400, step: 5, unit: "s", start: 60, momentum: true },
  m: { label: "Minutes", min: 1, max: 240, step: 1, unit: "min", start: 5, momentum: true },
  cal: { label: "Calories", min: 1, max: 500, step: 1, unit: "cal", start: 20, momentum: true },
  rounds: { label: "Rounds", min: 1, max: 60, step: 1, unit: "rounds", start: 3 },
  sets: { label: "Sets", min: 1, max: 20, step: 1, unit: "sets", start: 3 },
  workout: { label: "Times", min: 1, max: 10, step: 1, unit: "×", start: 1 },
};

/**
 * A time in seconds as the step stores it: whole minutes (a minute or more)
 * as minutes, anything else as seconds. So 30:00 is "30 min" and 1:30 is 90 s.
 */
export const splitTime = (seconds) => {
  const sec = Math.round(Number(seconds) || 0);
  return sec >= 60 && sec % 60 === 0 ? { unit: "m", value: sec / 60 } : { unit: "s", value: sec };
};

/** What a unit's stepper starts on when there's no value yet. */
export const startValueFor = (unit) => (AMOUNT[unit] || AMOUNT.reps).start;

/** The value of a step in its unit, on the draggable stepper. */
export const AmountStepper = ({ unit, value, onChange, label }) => {
  const a = AMOUNT[unit] || AMOUNT.reps;
  const v = Number.isFinite(Number(value)) && Number(value) > 0 ? Number(value) : a.start;
  return (
    <DraggableStepper
      label={label || a.label}
      value={v}
      onChange={onChange}
      min={a.min}
      max={a.max}
      step={a.step}
      unit={a.unit}
      momentum={a.momentum}
    />
  );
};

export const LOAD_UNITS = [
  { value: "kg", label: "kg" },
  { value: "pct", label: "% 1RM" },
];

/** Plate steps by kit: kettlebells in 4s, dumbbells in 2s, else 2.5. */
export const loadStepFor = (name) => (/kettlebell/i.test(name || "") ? 4 : /dumbbell/i.test(name || "") ? 2 : 2.5);

/**
 * A load (0 is none, or bodyweight) or a % of 1RM. Loads are kept in kg but
 * shown in the unit chosen in Profile (#191); `pounds` (a setter from the
 * caller's kg | lb toggle) overrides it for this field, so a plate read in
 * pounds can go in as pounds.
 */
export const LoadStepper = ({ value, onChange, loadUnit = "kg", name, offLabel = "None", shownUnit }) => {
  const preferred = useWeightUnit();
  const unit = loadUnit === "pct" ? "pct" : shownUnit || preferred;
  if (unit === "lb") {
    const step = /kettlebell/i.test(name || "") ? 9 : 5;
    return (
      <DraggableStepper
        label="Load"
        value={toDisplayWeight(Number(value) || 0, "lb")}
        onChange={(lb) => onChange(fromDisplayWeight(lb, "lb"))}
        min={0}
        max={880}
        step={step}
        unit="lb"
        offLabel={offLabel}
      />
    );
  }
  return (
    <DraggableStepper
      label="Load"
      value={Number(value) || 0}
      onChange={onChange}
      min={0}
      max={unit === "pct" ? 100 : 400}
      step={unit === "pct" ? 2.5 : loadStepFor(name)}
      unit={unit === "pct" ? "% 1RM" : "kg"}
      offLabel={offLabel}
    />
  );
};

/**
 * A load stepper with a small kg | lb switch under it (#191). The switch
 * starts on the Profile unit and only changes how this field reads.
 */
export const WeightLoadField = (props) => {
  const preferred = useWeightUnit();
  const [shown, setShown] = useState(null);
  const unit = shown || preferred;
  return (
    <div className="grid min-w-0 gap-1.5">
      <LoadStepper {...props} shownUnit={unit} />
      <div className="flex justify-end">
        <UnitToggle value={unit} onChange={setShown} />
      </div>
    </div>
  );
};

/** Every unit that applies, side by side. */
export const UnitChoice = ({ units, value, onChange, labels = UNIT_LABELS }) => (
  <div
    className="grid gap-1.5"
    style={{ gridTemplateColumns: `repeat(${Math.min(4, units.length)}, minmax(0, 1fr))` }}
    role="group"
    aria-label="Unit"
  >
    {units.map((u) => (
      <ChoiceChip key={u} selected={value === u} onClick={() => onChange(u)} className="px-2">
        {labels[u] || u}
      </ChoiceChip>
    ))}
  </div>
);

// The load and its unit: kg, lb or % 1RM side by side when % applies,
// else a small kg | lb switch (#191). lb is a way of reading kg, so picking
// it keeps the step's load in kg.
const LoadField = ({ load, onLoad, loadUnit, onLoadUnit, name, offLabel }) => {
  const preferred = useWeightUnit();
  const [shown, setShown] = useState(null);
  const weightUnit = shown || preferred;
  if (!onLoadUnit) return <WeightLoadField value={load} onChange={onLoad} name={name} offLabel={offLabel} />;
  const current = loadUnit === "pct" ? "pct" : weightUnit;
  const pick = (u) => {
    if (u === "pct") {
      onLoadUnit("pct");
      return;
    }
    setShown(u);
    if (loadUnit === "pct") onLoadUnit("kg");
  };
  return (
    <div className="grid min-w-0 gap-1.5">
      <LoadStepper value={load} onChange={onLoad} loadUnit={loadUnit} name={name} offLabel={offLabel} shownUnit={weightUnit} />
      <div className="grid grid-cols-3 gap-1.5">
        {[...LOAD_UNITS.slice(0, 1), { value: "lb", label: "lb" }, ...LOAD_UNITS.slice(1)].map((u) => (
          <ChoiceChip key={u.value} selected={current === u.value} onClick={() => pick(u.value)} className="py-1.5">
            {u.label}
          </ChoiceChip>
        ))}
      </div>
    </div>
  );
};

/**
 * A step's amount: the unit (when there's a choice), the value, and the load
 * (when `onLoad` is given) with its kg / % 1RM choice (when `onLoadUnit` is).
 * `disabled` greys the value and load, for when they're set per set instead.
 */
export default function StepAmountFields({
  units,
  unit,
  onUnit,
  value,
  onValue,
  load,
  onLoad,
  loadUnit = "kg",
  onLoadUnit,
  name,
  loadOffLabel = "None",
  disabled = false,
  disabledHint,
}) {
  // Seconds and Minutes are one "Time" choice when both apply (#181): the
  // stepper reads and types m:ss, and the step keeps whichever unit fits.
  const mergeTime = !!onUnit && units?.includes("s") && units?.includes("m");
  const isTime = mergeTime && (unit === "s" || unit === "m");
  const shownUnits = mergeTime ? units.filter((u) => u !== "m") : units;
  const pickUnit = (u) => {
    if (u === "s" && isTime) return; // already on Time
    onUnit(u);
  };
  const setTime = (seconds) => {
    const next = splitTime(seconds);
    if (next.unit !== unit) onUnit(next.unit);
    onValue(next.value);
  };
  return (
    <div className="grid min-w-0 gap-3">
      {shownUnits?.length > 1 && onUnit && (
        <UnitChoice
          units={shownUnits}
          value={isTime ? "s" : unit}
          onChange={mergeTime ? pickUnit : onUnit}
          labels={mergeTime ? { ...UNIT_LABELS, s: "Time" } : UNIT_LABELS}
        />
      )}
      <div className={cn("grid min-w-0 gap-3", disabled && "pointer-events-none opacity-50")} aria-disabled={disabled || undefined}>
        {isTime ? (
          <AmountStepper unit="time" value={unit === "m" ? Number(value) * 60 : value} onChange={setTime} />
        ) : (
          <AmountStepper unit={unit} value={value} onChange={onValue} />
        )}
        {onLoad && (
          <LoadField
            load={load}
            onLoad={onLoad}
            loadUnit={loadUnit}
            onLoadUnit={onLoadUnit}
            name={name}
            offLabel={loadOffLabel}
          />
        )}
      </div>
      {disabled && disabledHint && <p className="-mt-1 text-xs text-muted-foreground">{disabledHint}</p>}
    </div>
  );
}
